/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  LicenseInfoResponse,
  LicenseUploadResponse,
  LicenseValidityResponse,
  PostTablesSettingsAdministrationLicenseConsumptionCloudConstructs200,
  PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  PostTablesSettingsAdministrationLicenseConsumptionOnPremDevices200,
  PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  PostTablesSettingsAdministrationLicenseConsumptionOverview200,
  PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Returns information regarding the IP Fabric license installed on the system including devices count
 * @summary Return extended license information
 */
export const getLicensesInfo = (signal?: AbortSignal) => {
  return apiClient<LicenseInfoResponse>({
    url: `/licenses/info`,
    method: 'GET',
    signal,
  });
};

export const getGetLicensesInfoQueryKey = () => {
  return [`/licenses/info`] as const;
};

export const getGetLicensesInfoQueryOptions = <
  TData = Awaited<ReturnType<typeof getLicensesInfo>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getLicensesInfo>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLicensesInfoQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLicensesInfo>>> = ({
    signal,
  }) => getLicensesInfo(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLicensesInfo>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLicensesInfoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLicensesInfo>>
>;
export type GetLicensesInfoQueryError = unknown;

export function useGetLicensesInfo<
  TData = Awaited<ReturnType<typeof getLicensesInfo>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesInfo>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLicensesInfo>>,
          TError,
          Awaited<ReturnType<typeof getLicensesInfo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLicensesInfo<
  TData = Awaited<ReturnType<typeof getLicensesInfo>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesInfo>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLicensesInfo>>,
          TError,
          Awaited<ReturnType<typeof getLicensesInfo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLicensesInfo<
  TData = Awaited<ReturnType<typeof getLicensesInfo>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesInfo>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Return extended license information
 */

export function useGetLicensesInfo<
  TData = Awaited<ReturnType<typeof getLicensesInfo>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesInfo>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLicensesInfoQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Uploads the new license file for platform provided as the payload of the request
 * @summary Upload new license
 */
export const postLicensesUpload = (signal?: AbortSignal) => {
  return apiClient<LicenseUploadResponse>({
    url: `/licenses/upload`,
    method: 'POST',
    signal,
  });
};

export const getPostLicensesUploadMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLicensesUpload>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLicensesUpload>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['postLicensesUpload'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLicensesUpload>>,
    void
  > = () => {
    return postLicensesUpload();
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLicensesUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLicensesUpload>>
>;

export type PostLicensesUploadMutationError = void;

/**
 * @summary Upload new license
 */
export const usePostLicensesUpload = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postLicensesUpload>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postLicensesUpload>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getPostLicensesUploadMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns information regarding the IP Fabric license installed on the system
 * @summary Return license information
 */
export const getLicensesValidity = (signal?: AbortSignal) => {
  return apiClient<LicenseValidityResponse>({
    url: `/licenses/validity`,
    method: 'GET',
    signal,
  });
};

export const getGetLicensesValidityQueryKey = () => {
  return [`/licenses/validity`] as const;
};

export const getGetLicensesValidityQueryOptions = <
  TData = Awaited<ReturnType<typeof getLicensesValidity>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getLicensesValidity>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLicensesValidityQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLicensesValidity>>
  > = ({ signal }) => getLicensesValidity(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLicensesValidity>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLicensesValidityQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLicensesValidity>>
>;
export type GetLicensesValidityQueryError = unknown;

export function useGetLicensesValidity<
  TData = Awaited<ReturnType<typeof getLicensesValidity>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesValidity>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLicensesValidity>>,
          TError,
          Awaited<ReturnType<typeof getLicensesValidity>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLicensesValidity<
  TData = Awaited<ReturnType<typeof getLicensesValidity>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesValidity>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLicensesValidity>>,
          TError,
          Awaited<ReturnType<typeof getLicensesValidity>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLicensesValidity<
  TData = Awaited<ReturnType<typeof getLicensesValidity>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesValidity>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Return license information
 */

export function useGetLicensesValidity<
  TData = Awaited<ReturnType<typeof getLicensesValidity>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLicensesValidity>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLicensesValidityQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Table of all licensed cloud constructs.
 * @summary Licensed cloud constructs overview
 */
export const postTablesSettingsAdministrationLicenseConsumptionCloudConstructs =
  (
    postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
    signal?: AbortSignal,
  ) => {
    return apiClient<PostTablesSettingsAdministrationLicenseConsumptionCloudConstructs200>(
      {
        url: `/tables/settings/administration/license-consumption/cloud-constructs`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
        signal,
      },
    );
  };

export const getPostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryKey =
  (
    postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  ) => {
    return [
      `/tables/settings/administration/license-consumption/cloud-constructs`,
      postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
    ] as const;
  };

export const getPostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
      >
    >,
    TError = unknown,
  >(
    postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
            >
          >,
          TError,
          TData
        >
      >;
    },
  ) => {
    const { query: queryOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getPostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryKey(
        postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
        >
      >
    > = ({ signal }) =>
      postTablesSettingsAdministrationLicenseConsumptionCloudConstructs(
        postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
        signal,
      );

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
      >
    >
  >;
export type PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryError =
  unknown;

export function usePostTablesSettingsAdministrationLicenseConsumptionCloudConstructs<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionCloudConstructs<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionCloudConstructs<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Licensed cloud constructs overview
 */

export function usePostTablesSettingsAdministrationLicenseConsumptionCloudConstructs<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody: PostTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionCloudConstructs
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getPostTablesSettingsAdministrationLicenseConsumptionCloudConstructsQueryOptions(
      postTablesSettingsAdministrationLicenseConsumptionCloudConstructsBody,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Table of all licensed devices.
 * @summary Licensed devices overview
 */
export const postTablesSettingsAdministrationLicenseConsumptionOnPremDevices = (
  postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesSettingsAdministrationLicenseConsumptionOnPremDevices200>(
    {
      url: `/tables/settings/administration/license-consumption/on-prem-devices`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
      signal,
    },
  );
};

export const getPostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryKey =
  (
    postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  ) => {
    return [
      `/tables/settings/administration/license-consumption/on-prem-devices`,
      postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
    ] as const;
  };

export const getPostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
      >
    >,
    TError = unknown,
  >(
    postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
            >
          >,
          TError,
          TData
        >
      >;
    },
  ) => {
    const { query: queryOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getPostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryKey(
        postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
        >
      >
    > = ({ signal }) =>
      postTablesSettingsAdministrationLicenseConsumptionOnPremDevices(
        postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
        signal,
      );

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
      >
    >
  >;
export type PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryError =
  unknown;

export function usePostTablesSettingsAdministrationLicenseConsumptionOnPremDevices<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionOnPremDevices<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionOnPremDevices<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Licensed devices overview
 */

export function usePostTablesSettingsAdministrationLicenseConsumptionOnPremDevices<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody: PostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOnPremDevices
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getPostTablesSettingsAdministrationLicenseConsumptionOnPremDevicesQueryOptions(
      postTablesSettingsAdministrationLicenseConsumptionOnPremDevicesBody,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * License consumption overview table represents statistical overview of licensed devices and cloud constructs per type.
 * @summary License consumption overview
 */
export const postTablesSettingsAdministrationLicenseConsumptionOverview = (
  postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesSettingsAdministrationLicenseConsumptionOverview200>(
    {
      url: `/tables/settings/administration/license-consumption/overview`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: postTablesSettingsAdministrationLicenseConsumptionOverviewBody,
      signal,
    },
  );
};

export const getPostTablesSettingsAdministrationLicenseConsumptionOverviewQueryKey =
  (
    postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  ) => {
    return [
      `/tables/settings/administration/license-consumption/overview`,
      postTablesSettingsAdministrationLicenseConsumptionOverviewBody,
    ] as const;
  };

export const getPostTablesSettingsAdministrationLicenseConsumptionOverviewQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionOverview
      >
    >,
    TError = unknown,
  >(
    postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOverview
            >
          >,
          TError,
          TData
        >
      >;
    },
  ) => {
    const { query: queryOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getPostTablesSettingsAdministrationLicenseConsumptionOverviewQueryKey(
        postTablesSettingsAdministrationLicenseConsumptionOverviewBody,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionOverview
        >
      >
    > = ({ signal }) =>
      postTablesSettingsAdministrationLicenseConsumptionOverview(
        postTablesSettingsAdministrationLicenseConsumptionOverviewBody,
        signal,
      );

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof postTablesSettingsAdministrationLicenseConsumptionOverview
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type PostTablesSettingsAdministrationLicenseConsumptionOverviewQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postTablesSettingsAdministrationLicenseConsumptionOverview
      >
    >
  >;
export type PostTablesSettingsAdministrationLicenseConsumptionOverviewQueryError =
  unknown;

export function usePostTablesSettingsAdministrationLicenseConsumptionOverview<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOverview
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOverview
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOverview
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOverview
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionOverview<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOverview
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOverview
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOverview
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof postTablesSettingsAdministrationLicenseConsumptionOverview
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsAdministrationLicenseConsumptionOverview<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOverview
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOverview
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary License consumption overview
 */

export function usePostTablesSettingsAdministrationLicenseConsumptionOverview<
  TData = Awaited<
    ReturnType<
      typeof postTablesSettingsAdministrationLicenseConsumptionOverview
    >
  >,
  TError = unknown,
>(
  postTablesSettingsAdministrationLicenseConsumptionOverviewBody: PostTablesSettingsAdministrationLicenseConsumptionOverviewBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof postTablesSettingsAdministrationLicenseConsumptionOverview
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getPostTablesSettingsAdministrationLicenseConsumptionOverviewQueryOptions(
      postTablesSettingsAdministrationLicenseConsumptionOverviewBody,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
